/**
 * Shilajit PDP.
 *
 * Scoped to <sg-pdp>; every query runs against the element, so multiple
 * instances and Theme Editor section reloads stay independent.
 *
 * Offers are quantity-based: choosing one writes the real `quantity` field on
 * the product form, so the native /cart/add POST adds the units actually
 * priced. No pricing is invented — totals are the variant's price times the
 * chosen quantity.
 */
class SgPdp extends HTMLElement {
  connectedCallback() {
    // Shopify's {% form %} tag renders the <form>, so query it rather than
    // relying on an attribute we cannot place on it.
    this.form = this.querySelector('form.sg-buy');
    this.quantityInput = this.querySelector('[data-sg-quantity]');
    this.planInput = this.querySelector('[data-sg-selling-plan]');
    this.refillInput = this.querySelector('[data-sg-refill]');
    this.offerInputs = Array.from(this.querySelectorAll('[data-sg-offer]'));

    this.unitPrice = Number(this.dataset.unitPrice || 0);
    this.unitCompare = Number(this.dataset.unitCompare || 0);

    this.#initGallery();
    this.#initOffers();
    this.#initRefill();
    this.#initForm();
  }

  /* ---------------- Gallery ---------------- */

  #initGallery() {
    this.track = this.querySelector('[data-sg-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-sg-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-sg-thumb]'));
    this.prevBtn = this.querySelector('[data-sg-prev]');
    this.nextBtn = this.querySelector('[data-sg-next]');

    if (this.slides.length <= 1) {
      this.prevBtn?.setAttribute('hidden', '');
      this.nextBtn?.setAttribute('hidden', '');
      return;
    }

    this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.#goToSlide(i)));
    this.prevBtn?.addEventListener('click', () => this.#step(-1));
    this.nextBtn?.addEventListener('click', () => this.#step(1));

    // root is the scroller, so page scrolling never changes the active slide.
    this.galleryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) this.#setActiveSlide(this.slides.indexOf(e.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((s) => this.galleryObserver.observe(s));
    this.#setActiveSlide(0);
  }

  #goToSlide(i) {
    const slide = this.slides?.[i];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  #step(delta) {
    const next = Math.min(Math.max((this.activeSlide ?? 0) + delta, 0), this.slides.length - 1);
    this.#goToSlide(next);
  }

  #setActiveSlide(i) {
    if (i < 0) return;
    this.activeSlide = i;
    this.thumbs?.forEach((t, n) => {
      t.classList.toggle('is-active', n === i);
      t.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
    if (this.prevBtn) this.prevBtn.disabled = i <= 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;
  }

  /* ---------------- Offers ---------------- */

  #initOffers() {
    if (!this.offerInputs.length) return;
    this.offerInputs.forEach((i) => i.addEventListener('change', () => this.#onOfferChange()));
    this.#onOfferChange();
  }

  #onOfferChange() {
    const offer = this.offerInputs.find((i) => i.checked) || null;

    this.offerInputs.forEach((i) => {
      i.closest('[data-sg-offer-card]')?.classList.toggle('is-selected', i.checked);
    });

    const qty = Math.max(1, Number(offer?.dataset.sgQty || 1));
    if (this.quantityInput) this.quantityInput.value = String(qty);
    this.#renderCtaPrice(qty);
  }

  /* ---------------- Refill ---------------- */

  #initRefill() {
    if (!this.refillInput || !this.planInput) return;
    this.refillInput.addEventListener('change', () => this.#onRefillChange());
    this.#onRefillChange();
  }

  #onRefillChange() {
    const planId = this.refillInput.dataset.sgPlan || '';
    const on = this.refillInput.checked && planId !== '';
    // Disabled inputs are omitted from the POST, which is exactly what a
    // one-time purchase needs: never send an empty selling_plan.
    this.planInput.value = on ? planId : '';
    this.planInput.disabled = !on;
  }

  /* ---------------- Price ---------------- */

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  #renderCtaPrice(qty) {
    const priceEl = this.querySelector('[data-sg-cta-price]');
    if (!priceEl) return;
    priceEl.textContent = this.#money(this.unitPrice * qty);
  }

  /* ---------------- Cart ---------------- */

  #initForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', () => {
      const btn = this.querySelector('[data-sg-submit]');
      if (!btn || this.submitting) return;

      this.submitting = true;
      btn.setAttribute('aria-busy', 'true');
      // Not `disabled`: a disabled submit button is omitted from the POST and
      // the name="add" pair has to reach Shopify.
      btn.setAttribute('aria-disabled', 'true');

      window.addEventListener(
        'pageshow',
        () => {
          this.submitting = false;
          btn.removeAttribute('aria-busy');
          btn.removeAttribute('aria-disabled');
        },
        { once: true }
      );
    });
  }

  disconnectedCallback() {
    this.galleryObserver?.disconnect();
  }
}

if (!customElements.get('sg-pdp')) customElements.define('sg-pdp', SgPdp);
